import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Flex,
    Heading,
    Text,
    Image,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Collapse} from '@chakra-ui/react'
import {useProducts, usePromotions} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSelectedBonusQuantityForBonusDiscountLineItem} from '../../../utils/basket-utils'
import {useGlobaleContext} from '../../../contexts/globale'
import {getUrl} from '../../../utils/cloudinary-utils'
import {
    GWP_PROMOTION_ID_REGEX,
    SELECTED_ATTRIBUTES_WIDGET_PRODUCTS,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
} from '../../../constants'

import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import {Navigation} from 'swiper/modules'

import ProductTile from '../../../components/product-tile'
import LoadingSpinner from '../../../components/loading-spinner'
import useTagManager from '../../../hooks/use-tag-manager'
import {getSource} from '../../../utils/product-utils'

const PWP_PROMOTION_ID_REGEX = /pwp/i

const getSelectableAttributeId = (product) => {
    if (product?.variationValues?.[PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4]) {
        return PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
    }
    return product?.variationAttributes?.find((attr) =>
        [
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
        ].includes(attr?.id)
    )?.id
}

const PwpCarousel = ({basket}) => {
    const intl = useIntl()
    const {countryISO, currencyCode} = useGlobaleContext()
    const spaceBetween = 20
    const slidesPerView = useBreakpointValue({base: 2.5, md: 4.25, xl: 5.25})

    const pwpGroups = useMemo(() => {
        const bonusDiscountLineItems = basket?.bonusDiscountLineItems || []
        if (!bonusDiscountLineItems.length) return []

        const groupsByPromotionId = new Map()

        for (const bonusDiscountLineItem of bonusDiscountLineItems) {
            const promotionId = bonusDiscountLineItem?.promotionId
            if (!promotionId || GWP_PROMOTION_ID_REGEX.test(promotionId)) continue
            if (!PWP_PROMOTION_ID_REGEX.test(promotionId)) continue

            const selectedBonusQuantity = getSelectedBonusQuantityForBonusDiscountLineItem(
                basket,
                bonusDiscountLineItem?.id
            )

            const group = groupsByPromotionId.get(promotionId) || {
                promotionId,
                bonusDiscountLineItems: [],
                productIds: [],
                maxBonusItems: 0,
                selectedBonusQty: 0
            }

            group.bonusDiscountLineItems.push(bonusDiscountLineItem)
            group.maxBonusItems += bonusDiscountLineItem?.maxBonusItems || 0
            group.selectedBonusQty += selectedBonusQuantity

            const bonusProductIds = (bonusDiscountLineItem?.bonusProducts || [])
                .map((bonusProduct) => bonusProduct.productId)
                .filter(Boolean)
            group.productIds = [...new Set([...group.productIds, ...bonusProductIds])]

            groupsByPromotionId.set(promotionId, group)
        }

        return Array.from(groupsByPromotionId.values()).map((promoGroup) => ({
            ...promoGroup,
            remainingBonusQty: Math.max(0, promoGroup.maxBonusItems - promoGroup.selectedBonusQty)
        }))
    }, [basket])

    if (!pwpGroups.length) return null

    return (
        <Box
            id="cart-pwp-carousels"
            className="pwp-carousels"
            data-testid="cart-pwp-carousels"
            overflow="hidden"
            minWidth={0}
        >
            {pwpGroups.map((group) => (
                <PwpCarouselGroup
                    key={group.promotionId}
                    group={group}
                    basket={basket}
                    countryISO={countryISO}
                    currencyCode={currencyCode}
                    spaceBetween={spaceBetween}
                    slidesPerView={slidesPerView}
                    intl={intl}
                />
            ))}
        </Box>
    )
}

PwpCarousel.propTypes = {
    basket: PropTypes.object
}

const PwpCarouselGroup = ({group, countryISO, currencyCode, spaceBetween, slidesPerView, intl}) => {
    const tagManager = useTagManager()
    const styles = useMultiStyleConfig('PwpCarouselStyles')
    const [isOpen, setIsOpen] = useState(false)
    const [showSpinner, setShowSpinner] = useState(false)

    const {app: {commerceAPI} = {}} = getConfig() || {}
    let enableBloomreachSearch = false
    try {
        const bloomreachConfig = JSON.parse(commerceAPI?.bloomreach?.config)
        enableBloomreachSearch = !!bloomreachConfig?.enableBloomreachSearch
    } catch {
        // Bloomreach config not available or invalid
    }

    const {data: promoResponse} = usePromotions(
        {parameters: {ids: [group.promotionId]}},
        {enabled: !isServer && Boolean(group.promotionId)}
    )
    const promo = promoResponse?.data?.[0]

    const autoExpand = !!promo?.c_pwpCarouselAutoExpand

    useEffect(() => {
        if (autoExpand) {
            setIsOpen(true)
        }
    }, [autoExpand])

    const effectiveIsOpen = autoExpand || isOpen

    const {data: productsResponse, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: group.productIds.join(','),
                expand: ['availability', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: SELECTED_ATTRIBUTES_WIDGET_PRODUCTS,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {enabled: !isServer && group.productIds.length > 0}
    )

    const products = useMemo(
        () =>
            (productsResponse?.data || []).sort((productA, productB) => {
                const isOrderableA = !!productA?.inventory?.orderable
                const isOrderableB = !!productB?.inventory?.orderable
                if (isOrderableA === isOrderableB) return 0
                return isOrderableA ? -1 : 1
            }),
        [productsResponse]
    )

    const allProductsSoldOut =
        !isProductsLoading &&
        products.length > 0 &&
        products.every((product) => !product?.inventory?.orderable)

    const activeBonusDiscountLineItem = group.bonusDiscountLineItems.find(
        (bonusDiscountLineItem) => (bonusDiscountLineItem?.maxBonusItems || 0) > 0
    )
    const bonusDiscountLineItemId = activeBonusDiscountLineItem?.id

    const remainingBonusQuantity = group.remainingBonusQty
    const promoName = promo?.c_promotionHeader || promo?.name || ''
    const itemLabel = remainingBonusQuantity === 1 ? 'item' : 'items'
    const subHeading =
        promo?.c_promotionSubHeader ||
        intl.formatMessage(
            {
                id: 'pwp_carousel.select_up_to',
                defaultMessage: 'Select up to {count} {itemLabel}'
            },
            {count: remainingBonusQuantity, itemLabel}
        )

    useEffect(() => {
        if (allProductsSoldOut || remainingBonusQuantity <= 0) return null
        if (promo?.c_renderPwpCarousel) {
            const source = getSource()
            window.__dataLayerLoaded = true
            tagManager.promoMessageImpression({
                id: promo.id,
                name: promo.c_promotionHeader,
                creative:
                    (GWP_PROMOTION_ID_REGEX?.test(promo?.id) ? 'GWP' : 'PWP') +
                    ` Banner - Carousel`,
                position: source
            })
            window.__dataLayerLoaded = false
        }
    }, [promo])

    if (promo && !promo.c_renderPwpCarousel) return null
    if (allProductsSoldOut || remainingBonusQuantity <= 0) return null

    return (
        <Box
            {...styles.group}
            id={`cart-pwp-carousel-${group.promotionId}`}
            className="pwp-carousel-group"
            data-testid="cart-pwp-carousel"
            data-promo-id={group.promotionId}
        >
            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}

            <Box className="pwp-carousel-container" {...styles.container}>
                <Flex
                    {...styles.header}
                    className="pwp-carousel-header"
                    role="button"
                    tabIndex={0}
                    onClick={() => setIsOpen((prev) => !prev)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            setIsOpen((prev) => !prev)
                        }
                    }}
                >
                    <Box className="pwp-carousel-header-content" {...styles.headerContent}>
                        <Heading className="pwp-carousel-title" as="h3" {...styles.title}>
                            <Text
                                className="pwp-carousel-title-label"
                                as="span"
                                {...styles.titleLabel}
                            >
                                PWP Add-On Items:
                            </Text>{' '}
                            <Text
                                className="pwp-carousel-title-promo-name"
                                as="span"
                                {...styles.titlePromoName}
                            >
                                {promoName}
                            </Text>
                        </Heading>
                        <Text className="pwp-carousel-subtitle" {...styles.subtitle}>
                            {subHeading}
                        </Text>
                    </Box>
                    <Image
                        className="pwp-carousel-toggle-icon"
                        src={getUrl('UIImages/images/redesign/arrow-default.svg')}
                        alt="Toggle"
                        {...styles.toggleIcon}
                        transform={effectiveIsOpen ? 'rotate(270deg)' : 'rotate(90deg)'}
                    />
                </Flex>

                <Collapse in={effectiveIsOpen} animateOpacity>
                    <Box className="pwp-carousel-products" {...styles.productsWrapper}>
                        {isProductsLoading || !products.length ? (
                            <Flex className="pwp-carousel-skeleton" {...styles.skeleton}>
                                {Array.from({length: Math.floor(slidesPerView || 3)}).map(
                                    (_, index) => (
                                        <Box
                                            className="pwp-carousel-skeleton-item"
                                            key={index}
                                            {...styles.skeletonItem}
                                        />
                                    )
                                )}
                            </Flex>
                        ) : (
                            <Swiper
                                navigation
                                slidesPerView={slidesPerView}
                                spaceBetween={spaceBetween}
                                modules={[Navigation]}
                                observer
                                observeParents
                                className="pwp-carousel-swiper"
                            >
                                {products.map((product, idx) => {
                                    const selectableAttributeId = getSelectableAttributeId(product)
                                    return (
                                        <SwiperSlide
                                            key={product?.id || idx}
                                            className="pwp-carousel-slide"
                                        >
                                            <ProductTile
                                                dataTestid="pwp-carousel-item"
                                                product={product}
                                                tilesTitle="PWP Carousel"
                                                isBloomReachSearch={enableBloomreachSearch}
                                                showQuickAdd={remainingBonusQuantity > 0}
                                                pwpTargetPromotionId={group.promotionId}
                                                quickAddLabel={true}
                                                openQuickAddModal={true}
                                                showImageSlider={false}
                                                hideVariations={false}
                                                showBadge={false}
                                                showTextBadges={false}
                                                showPromotions={false}
                                                showPrice={false}
                                                enableFavourite={false}
                                                selectableAttributeId={selectableAttributeId}
                                                bonusDiscountLineItemId={bonusDiscountLineItemId}
                                                setShowAddtoCartSpinner={setShowSpinner}
                                                dynamicImageProps={{
                                                    imageProps: {aspectRatio: '119/149'}
                                                }}
                                                onViewAllClick={true}
                                                index={idx}
                                            />
                                        </SwiperSlide>
                                    )
                                })}
                            </Swiper>
                        )}
                    </Box>
                </Collapse>
            </Box>
        </Box>
    )
}

PwpCarouselGroup.propTypes = {
    group: PropTypes.shape({
        promotionId: PropTypes.string.isRequired,
        bonusDiscountLineItems: PropTypes.array.isRequired,
        productIds: PropTypes.array.isRequired,
        maxBonusItems: PropTypes.number,
        selectedBonusQty: PropTypes.number,
        remainingBonusQty: PropTypes.number
    }).isRequired,
    basket: PropTypes.object,
    countryISO: PropTypes.string,
    currencyCode: PropTypes.string,
    spaceBetween: PropTypes.number,
    slidesPerView: PropTypes.number,
    intl: PropTypes.object
}

export default PwpCarousel
